import { useState, useMemo, Fragment } from 'react';
import dayjs from 'dayjs';
import { useActivePlayer } from '@/context/ActivePlayerContext';
import { useToast } from '@/context/ToastContext';
import EditableTile from '@/common/EditableTile';
import EditableDropdown from '@/common/EditableDropdown';
import TextAreaInput from '@/common/TextArea/Input';
import DatePicker from '@/common/DatePicker/DatePicker';
import SpinnerOverlay from '@/common/SpinnerOverlay';
import { SavePlayerAddressPayload, SavePlayerBioPayload, SavePlayerPayload, SavePlayerPhonePayload } from '@/api/models';
import Phone from '@/api/models/Phone';
import { PlayerHelper } from '@/library';
import { Address } from '@/api/models/Address';
import { useEditPlayerInfo } from './useEditPlayerInfo';
import { sortByDefault } from './PlayerInfo';
import PhoneModal from './PhoneModal';
import AddressModal, { IAddressData } from './AddressModal';
import EditPadIcon from '@/assets/icons/edit-pad.svg?react';
import GreenCheckmarkIcon from '@/assets/icons/green-checkmark.svg?react';
import clubStyles from '../Club.module.scss';
import styles from './PlayerInfo.module.scss';

const formatDate = (date: string) => dayjs(date).format('MM/DD/YYYY');

export interface IPhoneModalState {
  mode: 'create' | 'edit';
  phone?: Phone;
}

export interface IAddressModalState {
  mode: 'create' | 'edit';
  address?: Address;
}

interface IEditPlayerInfo {
  handleCancel: () => void;
}

function EditPlayerInfo({ handleCancel }: IEditPlayerInfo) {
  const { player, setPlayer } = useActivePlayer();
  const { showToast } = useToast();
  const [phoneModalState, setPhoneModalState] = useState<IPhoneModalState | null>(null);
  const [addressModalState, setAddressModalState] = useState<IAddressModalState | null>(null);
  const [showLoading, setShowLoading] = useState(false);
  const [firstName, setFirstName] = useState(player?.firstName || '');
  const [lastName, setLastName] = useState(player?.lastName || '');
  const [email, setEmail] = useState(player?.playerBioDto?.email || '');
  const [birthday, setBirthday] = useState(player?.playerBioDto?.dob?.toString() || '');
  const [tourId, setTourId] = useState<string>(player?.defaultTourId?.toString() || '');
  const [playerStatusLookupId, setPlayerStatusLookupId] = useState<string>(player?.playerBioDto.playerStatusLookupId?.toString() || '');
  const [playerRegionLookupId, setPlayerRegionLookupId] = useState<string>(player?.playerBioDto.playerRegionLookupId?.toString() || '');
  const [playerInternalStatusId, setPlayerInternalStatusId] = useState<string>(player?.playerBioDto.playerInternalStatusLookupId?.toString() || '');
  const [nationality, setNationality] = useState<string>(player?.playerBioDto.nationality?.toString() || '');
  const [manager, setManager] = useState<string>(player?.playerBioDto.manager || '');
  const [caddy, setCaddy] = useState<string>(player?.playerBioDto.caddy || '');
  const [collegeId, setCollegeId] = useState<string>(player?.playerBioDto.playerCollegeLookupId?.toString() || '');
  const [yearId, setYearId] = useState<string>(player?.playerBioDto.playerYearLookupId?.toString() || '');
  const [supportId, setSupportId] = useState<string>(player?.playerBioDto.playerSupportLookupId?.toString() || '');
  const [notes, setNotes] = useState<string>(player?.playerBioDto?.notes || '');
  const [fedExPlayerId, setFedExPlayerId] = useState(player?.currentPlayerRankingsPlayerId || '');
  const { loading, tours, playerStatusLookups, playerRegionLookups, playerInternalStatuses, internalStatusLookups, nationalities, colleges, years, supports, fedExCupPlayers } = useEditPlayerInfo();

  const playerInternalStatusOptions = useMemo(() => {
    if (loading) return [];

    return playerInternalStatuses
      .filter((internalStatus) => internalStatus.playerStatusLookupId === Number(playerStatusLookupId))
      .map((internalStatus) => {
        const internalStatusLookup = internalStatusLookups.find((statusLookup) => statusLookup.playerInternalStatusLookupId === internalStatus.playerInternalStatusLookupId);
        if (!internalStatusLookup) throw new Error('Failed to find the internalStatusLookup in EditPlayerInfo component');

        return { id: internalStatusLookup.playerInternalStatusLookupId.toString(), label: internalStatusLookup.name };
      });
  }, [playerStatusLookupId, loading, playerInternalStatuses, internalStatusLookups]);

  const savePlayerInfo = async () => {
    if (!player) throw new Error('Edit Player info missing player');

    setShowLoading(true);
    const savePlayerPayload: SavePlayerPayload = {
      defaulttourid: Number(tourId),
      defaulttourname: tours.find((tour) => tour.tourId === Number(tourId))?.name || '',
      editMode: true,
      ia: player.isActive,
      fn: firstName.trim(),
      full: `${firstName.trim()} ${lastName.trim()}`,
      ln: lastName.trim(),
      pclname: colleges.find((college) => college.playerCollegeLookupId === Number(collegeId))?.name || '',
      pid: player.playerId,
      pislname: internalStatusLookups.find((internalStatus) => internalStatus.playerInternalStatusLookupId === Number(playerInternalStatusId))?.name || '',
      prlname: playerRegionLookups.find((playerRegion) => playerRegion.playerRegionLookupId === Number(playerRegionLookupId))?.name || '',
      pslname: playerStatusLookups.find((playerStatus) => playerStatus.playerStatusLookupId === Number(playerStatusLookupId))?.name || '',
      psulname: supports.find((support) => support.playerSupportLookupId === Number(supportId))?.name || '',
      pylname: years.find((year) => year.playerYearLookupId === Number(yearId))?.name || '',
      tourNames: tours.find((tour) => tour.tourId === Number(tourId))?.name || '',
      tours: [Number(tourId)],
      toursList: [{ tourid: Number(tourId) }],
      currentPlayerRankingsPlayerId: fedExPlayerId,
      playerBio: {
        playerBioId: player.playerBioDto.playerBioId,
        playerCollegeLookupId: Number(collegeId),
        playerInternalStatusLookupId: Number(playerInternalStatusId),
        playerRegionLookupId: Number(playerRegionLookupId),
        playerStatusLookupId: Number(playerStatusLookupId),
        playerSupportLookupId: Number(supportId),
        playerYearLookupId: Number(yearId),
      },
      playerbio: {
        ...(birthday && { dob: formatDate(birthday) }),
        pbid: player.playerBioDto.playerBioId,
        pclid: Number(collegeId),
        pclname: colleges.find((college) => college.playerCollegeLookupId === Number(collegeId))?.name || '',
        pid: player.playerId,
        pislid: Number(playerInternalStatusId),
        pislname: internalStatusLookups.find((internalStatus) => internalStatus.playerInternalStatusLookupId === Number(playerInternalStatusId))?.name || '',
        prlid: Number(playerRegionLookupId),
        prlname: playerRegionLookups.find((playerRegion) => playerRegion.playerRegionLookupId === Number(playerRegionLookupId))?.name || '',
        pslid: Number(playerStatusLookupId),
        pslname: playerStatusLookups.find((playerStatus) => playerStatus.playerStatusLookupId === Number(playerStatusLookupId))?.name || '',
        psulid: Number(supportId),
        psulname: supports.find((support) => support.playerSupportLookupId === Number(supportId))?.name || '',
        pylid: Number(yearId),
        pylname: years.find((year) => year.playerYearLookupId === Number(yearId))?.name || '',
        mgr: manager,
      },
    };

    const savePlayerBioPayload: SavePlayerBioPayload = {
      ...(birthday && { dob: formatDate(birthday) }),
      caddy,
      email: email,
      fullName: `${firstName.trim()} ${lastName.trim()}`,
      isActive: player.isActive,
      manager,
      nationality,
      notes,
      playerBioId: player.playerBioDto.playerBioId,
      playerCollegeLookupId: Number(collegeId),
      playerCollegeLookupName: colleges.find((college) => college.playerCollegeLookupId === Number(collegeId))?.name || '',
      playerId: player.playerId,
      playerInternalStatusLookupId: Number(playerInternalStatusId),
      playerInternalStatusLookupName: internalStatusLookups.find((internalStatus) => internalStatus.playerInternalStatusLookupId === Number(playerInternalStatusId))?.name || '',
      playerRegionLookupId: Number(playerRegionLookupId),
      playerRegionLookupName: playerRegionLookups.find((playerRegion) => playerRegion.playerRegionLookupId === Number(playerRegionLookupId))?.name || '',
      playerStatusLookupId: Number(playerStatusLookupId),
      playerStatusLookupName: playerStatusLookups.find((playerStatus) => playerStatus.playerStatusLookupId === Number(playerStatusLookupId))?.name || '',
      playerSupportLookupId: Number(supportId),
      playerSupportLookupName: supports.find((support) => support.playerSupportLookupId === Number(supportId))?.name || '',
      playerYearLookupId: Number(yearId),
      playerYearLookupName: years.find((year) => year.playerYearLookupId === Number(yearId))?.name || '',
    };

    try {
      const updatedPlayer = await PlayerHelper.editPlayer({ playerId: player.playerId, savePlayerPayload, savePlayerBioPayload });
      setPlayer({ ...updatedPlayer, activeModule: 'Player Info' }); // update local player state and retain current active module
      showToast({ message: 'Player Updated Successfully', showCheckmark: true });
    } catch (error) {
      console.error(`Error while editing a Player: ${error}`);
    }
    setShowLoading(false);
    handleCancel();
  };

  const handleSavePlayerPhone = async (phoneData: { label: string; number: string; isDefault: boolean }) => {
    if (!player || !phoneModalState) throw new Error('Missing player or phone data for updating a player phone');
    try {
      setShowLoading(true);
      const payload: SavePlayerPhonePayload = {
        code: phoneData.label,
        isActive: true,
        isDefault: phoneData.isDefault,
        phoneId: phoneModalState?.mode === 'create' ? -1 : phoneModalState?.phone?.phoneId || 0, // -1 when creating a new phone
        phoneNo: phoneData.number,
        phoneTypeId: phoneModalState?.mode === 'create' ? -1 : 1, // -1 when creating a new phone, 1 when editing
        playerId: player?.playerId,
        ...(phoneModalState.mode === 'edit' && { editMode: true }),
      };
      const updatedPlayer = await PlayerHelper.savePlayerPhone(payload);
      setPlayer({ ...updatedPlayer, activeModule: 'Player Info' }); // update local player state and retain current active module
      setPhoneModalState(null);
      showToast({ message: 'Player Phone Updated Successfully', showCheckmark: true });
    } catch (error) {
      console.error(`Error when trying to save a player phone: ${error}`);
    } finally {
      setShowLoading(false);
    }
  };

  const handleSavePlayerAddress = async (addressData: IAddressData) => {
    if (!player || !addressModalState) throw new Error('Missing player or address data for updating a player phone');
    try {
      setShowLoading(true);
      const payload: SavePlayerAddressPayload = {
        code: addressData.code,
        isActive: addressData.isActive,
        isDefault: addressData.isDefault,
        addressId: addressModalState?.mode === 'create' ? -1 : addressModalState?.address?.addressId || 0, // -1 when creating a new address
        address1: addressData.address1,
        addressTypeId: addressModalState?.mode === 'create' ? -1 : 1, // -1 when creating a new address, 1 when editing
        playerId: player?.playerId,
        address2: addressData.address2,
        city: addressData.city,
        country: addressData.country,
        state: addressData.state,
        postalCode: addressData.postalCode,
        ...(addressModalState.mode === 'edit' && { editMode: true }),
      };
      const updatedPlayer = await PlayerHelper.savePlayerAddress(payload);
      setPlayer({ ...updatedPlayer, activeModule: 'Player Info' }); // update local player state and retain current active module
      setAddressModalState(null);
      showToast({ message: 'Player Address Updated Successfully', showCheckmark: true });
    } catch (error) {
      console.error(`Error when trying to save a player address: ${error}`);
    } finally {
      setShowLoading(false);
    }
  };

  if (loading) return <span className={styles.loading}>Loading...</span>;

  return (
    <div className={`${styles.container} ${styles.editPlayerBackground}`} data-testid="player-info-edit-root">
      <div className={styles.form}>
        <div className={styles.row}>
          <div className={clubStyles.title}>Edit Player</div>
        </div>
        <div className={clubStyles.tileRow}>
          <EditableTile label="First Name" value={firstName} onChange={(newFirstName: string) => setFirstName(newFirstName)} variant="large" />
          <EditableTile label="Last Name" value={lastName} onChange={(newLastName: string) => setLastName(newLastName)} variant="large" />
        </div>
        <div className={styles.row}>
          <div className={clubStyles.title}>Designation</div>
        </div>
        <div className={styles.tileRow}>
          <EditableDropdown
            label="Account"
            options={tours.map((tour) => ({ id: tour.tourId.toString(), label: tour.name }))}
            initialId={tourId}
            onChange={(newTourId) => setTourId(newTourId)}
            variant="large"
          />
          <EditableDropdown
            label="Player Status"
            initialId={playerStatusLookupId}
            options={playerStatusLookups.map((status) => ({ id: status.playerStatusLookupId.toString(), label: status.name }))}
            onChange={(newPlayerStatusLookupId) => setPlayerStatusLookupId(newPlayerStatusLookupId)}
            variant="large"
          />
          <EditableDropdown
            label="Region"
            initialId={playerRegionLookupId}
            options={playerRegionLookups.map((region) => ({ id: region.playerRegionLookupId.toString(), label: region.name }))}
            onChange={(newPlayerRegionLookupId) => setPlayerRegionLookupId(newPlayerRegionLookupId)}
            variant="large"
          />
          <EditableDropdown
            label="Internal Status"
            initialId={playerInternalStatusId}
            options={playerInternalStatusOptions}
            onChange={(newPlayerInternalStatusId) => setPlayerInternalStatusId(newPlayerInternalStatusId)}
            variant="large"
          />
        </div>
        <div className={styles.row}>
          <div className={clubStyles.title}>Address</div>
          <span role="button" onClick={() => setAddressModalState({ mode: 'create' })} className={styles.addNew} aria-label="Add New Address">
            + Add New
          </span>
        </div>
        <div className={styles.tileRow}>
          {player?.addresses?.sort(sortByDefault).map((address, index) => (
            <Fragment key={`${address.addressId}-${index}`}>
              <div className={`${styles.tile} ${styles.address} ${styles.modalTile}`}>
                <div className={`${styles.tileCode} ${styles.tileEdit}`}>
                  <span className={styles.tileTitle}>
                    {address.code} {address.isDefault ? <GreenCheckmarkIcon /> : ''}
                  </span>
                  <button type="button" className={styles.iconButton} aria-label="Edit address" onClick={() => setAddressModalState({ mode: 'edit', address })}>
                    <EditPadIcon aria-hidden="true" className={styles.icon} />
                  </button>
                </div>
                <div className={styles.tileText}>{address.address1}</div>
                <div className={styles.tileText}>{address.address2}</div>
                <div className={styles.tileText}>
                  {address.city} {address.state}, {address.postalCode}
                </div>
                <div className={styles.tileText}>{address.country}</div>
              </div>
            </Fragment>
          ))}
        </div>
        <div className={styles.row}>
          <div className={clubStyles.title}>Phone</div>
          <span role="button" onClick={() => setPhoneModalState({ mode: 'create' })} className={styles.addNew} aria-label="Add New Phone">
            + Add New
          </span>
        </div>
        <div className={styles.tileRow}>
          {player?.phonesResponse?.sort(sortByDefault).map((phone, index) => (
            <Fragment key={`${phone.phoneId}-${index}`}>
              <div className={`${styles.tile} ${styles.modalTile}`}>
                <div className={`${styles.tileCode} ${styles.tileEdit}`}>
                  <span className={styles.tileTitle}>
                    {phone.code} {phone.isDefault ? <GreenCheckmarkIcon /> : ''}
                  </span>
                  <button type="button" className={styles.iconButton} aria-label="Edit phone" onClick={() => setPhoneModalState({ mode: 'edit', phone })}>
                    <EditPadIcon aria-hidden="true" className={styles.icon} />
                  </button>
                </div>
                <div className={styles.tileText}>{phone.phoneNo}</div>
              </div>
            </Fragment>
          ))}
        </div>
        <div className={styles.row}>
          <div className={clubStyles.title}>General</div>
        </div>
        <div className={styles.tileRow}>
          <div className={`${styles.tile} ${styles.medium} ${styles.datePicker}`}>
            <div className={styles.tileLabel}>Birthday</div>
            <DatePicker value={birthday} handleOnChange={(event) => setBirthday(event.target.value)} />
          </div>
          <EditableTile label="Email" value={email} onChange={(newEmail) => setEmail(newEmail)} variant="xxl" />
          <EditableDropdown
            label="Nationality"
            initialId={nationality}
            options={nationalities.map(({ nationality }) => ({ id: nationality.toString(), label: nationality.toString() }))}
            onChange={(newNationality) => setNationality(newNationality)}
            variant="large"
          />
        </div>
      </div>
      <div className={styles.tileRow}>
        <div className={styles.tileRow}>
          <EditableTile label="Manager" value={manager} onChange={(newManager) => setManager(newManager)} variant="large" />
          <EditableTile label="Caddy" value={caddy} onChange={(newCaddy) => setCaddy(newCaddy)} variant="large" />
          <EditableDropdown
            label="College"
            initialId={collegeId}
            options={colleges.map((college) => ({ id: college.playerCollegeLookupId.toString(), label: college.name }))}
            onChange={(newCollegeId) => setCollegeId(newCollegeId)}
            variant="xxl"
          />
          <EditableDropdown
            label="Year"
            initialId={yearId}
            options={years.map((year) => ({ id: year.playerYearLookupId.toString(), label: year.name }))}
            onChange={(newYearId) => setYearId(newYearId)}
            variant="large"
          />
          <EditableDropdown
            label="Support"
            initialId={supportId}
            options={supports.map((support) => ({ id: support.playerSupportLookupId.toString(), label: support.name }))}
            onChange={(newSupportId) => setSupportId(newSupportId)}
            variant="xxl"
          />
        </div>
        <div className={styles.row}>
          <div className={clubStyles.title}>FedEx Cup and OWGR</div>
        </div>
        <div className={styles.tileRow}>
          <EditableDropdown
            label="FedEx Player Name"
            initialId={fedExPlayerId}
            options={fedExCupPlayers.sort((a, b) => a.fullName.localeCompare(b.fullName)).map((player) => ({ id: player.fedexCupId.toString(), label: player.fullName }))}
            onChange={(id) => setFedExPlayerId(id)}
            variant="large"
          />
        </div>
        <div className={styles.row}>
          <div className={clubStyles.title}>Player Notes</div>
        </div>
        <div className={`${styles.tileRow} ${styles.rowWidth}`}>
          <TextAreaInput value={notes} onChange={(newNote) => setNotes(newNote)} variant="fullSize" className={styles.notes} />
        </div>
      </div>
      <div className={clubStyles.buttonRow}>
        <button className={clubStyles.saveButton} onClick={savePlayerInfo}>
          Save
        </button>
        <button className={clubStyles.cancelButton} onClick={handleCancel}>
          Cancel
        </button>
      </div>
      {Boolean(phoneModalState) && (
        <PhoneModal
          isOpen={Boolean(phoneModalState)}
          playerName={player?.fullName || ''}
          phoneCodes={player?.phonesResponse.map((phone) => phone.code.trim().toLowerCase())}
          mode={phoneModalState?.mode}
          phone={phoneModalState?.phone}
          onClose={() => setPhoneModalState(null)}
          onSave={(phoneData) => handleSavePlayerPhone(phoneData)}
        />
      )}
      {Boolean(addressModalState) && (
        <AddressModal
          isOpen={Boolean(addressModalState)}
          playerName={player?.fullName || ''}
          addressCodes={player?.addresses.map((address) => address.code.trim().toLowerCase())}
          mode={addressModalState?.mode}
          address={addressModalState?.address}
          nationalities={nationalities}
          onClose={() => setAddressModalState(null)}
          onSave={(addressData) => handleSavePlayerAddress(addressData)}
        />
      )}
      {showLoading && (
        <div className={styles.loading}>
          <SpinnerOverlay />
        </div>
      )}
    </div>
  );
}

export default EditPlayerInfo;
